<template>
  <div data-test-id="create_session-popup-new-session">
    <DialogHeader>
      <DialogTitle>
        {{ sessionIdRef ? t('session.newSessionModal.modifySession.title') : t('session.newSessionModal.title') }}
      </DialogTitle>
    </DialogHeader>

    <form class="mt-6 flex flex-col gap-6" @submit.prevent="onSubmit">
      <ProgramReminder :programId="selectedProgramIdRef" />

      <LoadingContent v-if="sessionIdRef && isSessionPendingRef" />

      <div v-else class="flex flex-col gap-4">
        <!-- 1. Groupe (création uniquement) -->
        <FormField v-if="!sessionRef?.mentoringGroupId" v-slot="{ componentField }" name="groupId">
          <FormItem>
            <FormLabel>{{ t('session.newSessionModal.generalInformation.input.group.label') }} *</FormLabel>
            <Select v-bind="componentField">
              <FormControl>
                <SelectTrigger data-test-id="create_session-input-select-group" class="w-full">
                  <SelectValue :placeholder="t('ui.select.defaultOption')" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem v-for="g in groupsOptionsRef" :key="g.value" :value="g.value">
                  {{ g.label }}
                </SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        </FormField>

        <!-- Mentees preview juste sous le groupe -->
        <div v-if="menteesRef?.length" class="flex items-center gap-3 rounded-md border bg-muted/30 px-3 py-2">
          <div class="flex -space-x-2">
            <Avatar v-for="m in menteesRef.slice(0, 5)" :key="m.id" class="size-7 border-2 border-background">
              <AvatarFallback class="text-xs">{{ userInitials(m) }}</AvatarFallback>
            </Avatar>
          </div>
          <span class="text-sm text-muted-foreground">
            {{ menteesDisplayLabel }}
          </span>
        </div>

        <!-- 2. Objectif de session -->
        <FormField v-slot="{ componentField }" name="objectiveId">
          <FormItem>
            <FormLabel>{{ t('session.newSessionModal.generalInformation.input.objective.label') }}</FormLabel>
            <Select v-bind="componentField" :disabled="!values.groupId">
              <FormControl>
                <SelectTrigger data-test-id="create_session-input-select-objective" class="w-full">
                  <SelectValue :placeholder="t('session.newSessionModal.generalInformation.input.objective.none')" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem v-for="o in objectiveItemsRef" :key="o.value" :value="o.value">
                  {{ o.label }}
                </SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        </FormField>

        <!-- 3. Date + Horaire (row) -->
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormField v-slot="{ componentField }" name="date">
            <FormItem>
              <FormLabel>{{ t('session.newSessionModal.generalInformation.input.date.label') }} *</FormLabel>
              <FormControl>
                <Input
                  data-test-id="create_session-input-session-date"
                  type="date"
                  :min="
                    selectedProgramRef?.latestPeriod?.startAt
                      ? dayjsClient(selectedProgramRef.latestPeriod.startAt).format('YYYY-MM-DD')
                      : undefined
                  "
                  :max="
                    selectedProgramRef?.latestPeriod?.endAt
                      ? dayjsClient(selectedProgramRef.latestPeriod.endAt).format('YYYY-MM-DD')
                      : undefined
                  "
                  v-bind="componentField"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>

          <FormField v-slot="{ componentField }" name="schedule">
            <FormItem>
              <FormLabel>{{ t('session.newSessionModal.generalInformation.input.schedule.label') }} *</FormLabel>
              <FormControl>
                <TimePicker
                  data-test-id="create_session-input-session-time"
                  :placeholder="t('session.newSessionModal.generalInformation.input.schedule.placeholder')"
                  v-bind="componentField"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>
        </div>

        <!-- 4. Durée + helper heure de fin -->
        <FormField v-slot="{ componentField }" name="duration">
          <FormItem>
            <FormLabel>{{ t('session.newSessionModal.generalInformation.input.duration.label') }} *</FormLabel>
            <Select v-bind="componentField">
              <FormControl>
                <SelectTrigger data-test-id="create_session-select-session-duration" class="w-full">
                  <SelectValue :placeholder="t('ui.select.defaultOption')" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem v-for="d in DURATION_OPTIONS" :key="d.value" :value="d.value">
                  {{ d.label }}
                </SelectItem>
              </SelectContent>
            </Select>
            <FormDescription v-if="endTimeRef">
              {{ t('session.newSessionModal.endsAt', { time: endTimeRef }) }}
            </FormDescription>
            <FormMessage />
          </FormItem>
        </FormField>

        <!-- 5. Type de session -->
        <FormField v-slot="{ value, handleChange }" name="type">
          <FormItem>
            <FormLabel>{{ t('session.newSessionModal.generalInformation.input.type.label') }} *</FormLabel>
            <FormControl>
              <ToggleGroup
                type="single"
                variant="outline"
                :model-value="value"
                :aria-label="t('session.newSessionModal.generalInformation.input.type.label')"
                data-test-id="create_session-select-session-location"
                class="grid w-full grid-cols-2"
                @update:model-value="(v) => v && handleChange(v)"
              >
                <ToggleGroupItem
                  value="visio"
                  data-test-id="create_session-type-visio"
                  class="w-full data-[state=on]:bg-primary/10 data-[state=on]:text-primary data-[state=on]:hover:bg-primary/10 data-[state=on]:hover:text-primary"
                >
                  <Video class="size-4" />
                  {{ t('session.type.online') }}
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="in-person"
                  data-test-id="create_session-type-in-person"
                  class="w-full data-[state=on]:bg-primary/10 data-[state=on]:text-primary data-[state=on]:hover:bg-primary/10 data-[state=on]:hover:text-primary"
                >
                  <MapPin class="size-4" />
                  {{ t('session.type.irl') }}
                </ToggleGroupItem>
              </ToggleGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <!-- 6. Lieu (toujours visible, disabled si visio) -->
        <FormField v-slot="{ componentField }" name="location">
          <FormItem>
            <FormLabel>
              {{ t('session.newSessionModal.generalInformation.input.location.label') }}
              <span v-if="values.type === 'in-person'">*</span>
            </FormLabel>
            <FormControl>
              <Input
                data-test-id="create_session-input-text-location"
                type="text"
                :disabled="values.type !== 'in-person'"
                :placeholder="
                  values.type === 'in-person'
                    ? t('session.newSessionModal.generalInformation.input.location.placeholder')
                    : t('session.newSessionModal.locationDisabledPlaceholder')
                "
                v-bind="componentField"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <!-- 7. Nom (optionnel, bas de formulaire) -->
        <FormField v-slot="{ componentField }" name="name">
          <FormItem>
            <FormLabel class="flex items-center gap-2">
              {{ t('session.newSessionModal.generalInformation.input.name.label') }}
              <span class="text-muted-foreground text-xs font-normal"> ({{ t('ui.input.label.optional') }}) </span>
            </FormLabel>
            <FormControl>
              <Input
                data-test-id="create_session-input-session-name"
                type="text"
                :placeholder="t('session.newSessionModal.generalInformation.input.name.placeholder')"
                v-bind="componentField"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" @click="onClose">
          {{ t('ui.button.cancel') }}
        </Button>
        <Button type="submit" data-test-id="create_session-button-session-submit" :disabled="isSubmittingRef">
          <Loader2 v-if="isSubmittingRef" class="size-4 animate-spin" />
          <CalendarPlus v-else class="size-4" />
          {{ sessionIdRef ? t('session.newSessionModal.modify') : t('session.newSessionModal.plan') }}
        </Button>
      </DialogFooter>
    </form>
  </div>
</template>

<script setup lang="ts">
import { useMutation, useQuery } from '@tanstack/vue-query'
import { toTypedSchema } from '@vee-validate/zod'
import { CalendarPlus, Loader2, MapPin, Video } from 'lucide-vue-next'
import { useForm } from 'vee-validate'
import { computed, ref, watch } from 'vue'
import { toast } from 'vue-sonner'
import { z } from 'zod'

import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { TimePicker } from '@/components/ui/time-picker'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import useAccount from '@/features/auth/hooks/use-account.hook'
import { sendMessageMutation } from '@/features/chat/api'
import useGroup from '@/features/groups/hooks/use-group.hook'
import useGroups from '@/features/groups/hooks/use-groups.hook'
import {
  selectGetTempSelectedGroupId,
  selectGetTempSelectedObjectiveId,
} from '@/features/groups/stores/groups/groups.selectors'
import groupsStore from '@/features/groups/stores/groups/groups.store'
import { getMentees, getMentor } from '@/features/groups/utils'
import {
  getSessionObjectivesWithProgress,
  SESSION_OBJECTIVES_QUERY_KEY,
} from '@/features/programs/api/session-objectives.api'
import ProgramReminder from '@/features/programs/components/program-reminder/program-reminder.vue'
import useProgram from '@/features/programs/hooks/use-program.hook'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import { V1CreateSessionMutation, V1UpdateSessionMutation } from '@/features/sessions/api'
import useSession from '@/features/sessions/hooks/use-session.hook'
import LoadingContent from '@/features/ui/components/loading-content/loading-content.vue'
import { selectGetEditSessionModal } from '@/features/ui/store/modal/modal.selectors'
import type { EditSessionModalType, SessionDetailsModalType } from '@/features/ui/store/modal/modal.store'
import modalsStore from '@/features/ui/store/modal/modal.store'
import useRouter from '@/hooks/use-router.hook'
import dayjsClient from '@/lib/dayjs'
import { useI18n } from '@/lib/i18n'
import { ROUTES } from '@/utils/config/constants'
import {
  invalidateConversations,
  invalidateSession,
  invalidateSessionObjectives,
  invalidateSessions,
} from '@/utils/lib/vue-query'

const { t } = useI18n()
const router = useRouter()

const editSessionModalRef = selectGetEditSessionModal(modalsStore)
const sessionIdRef = computed(() => editSessionModalRef.value?.attachedData.sessionId || null)
const { sessionRef, isPendingRef: isSessionPendingRef } = useSession(sessionIdRef)
const { groupsRef } = useGroups({ perPageRef: ref(9999) })
const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)
const { programRef: selectedProgramRef } = useProgram(selectedProgramIdRef)
const tempSelectedGroupIdRef = selectGetTempSelectedGroupId(groupsStore)
const tempSelectedObjectiveIdRef = selectGetTempSelectedObjectiveId(groupsStore)
const { accountRef } = useAccount()

const DURATION_OPTIONS = [
  { value: '0.5', label: '30min' },
  { value: '0.75', label: '45min' },
  { value: '1', label: '1h' },
  { value: '1.5', label: '1h30' },
  { value: '2', label: '2h' },
  { value: '2.5', label: '2h30' },
  { value: '3', label: '3h' },
  { value: '3.5', label: '3h30' },
  { value: '4', label: '4h' },
  { value: '4.5', label: '4h30' },
  { value: '5', label: '5h' },
  { value: '5.5', label: '5h30' },
  { value: '6', label: '6h' },
] as const

const formSchema = toTypedSchema(
  z
    .object({
      name: z.string().nullable().default(null),
      groupId: z
        .string({ required_error: t('session.newSessionModal.validation.group.required') })
        .min(1, t('session.newSessionModal.validation.group.required')),
      objectiveId: z.string().nullable().default(null),
      date: z
        .string({ required_error: t('session.newSessionModal.validation.date.required') })
        .min(1, t('session.newSessionModal.validation.date.required')),
      type: z.enum(['visio', 'in-person'], {
        required_error: t('session.newSessionModal.validation.type.required'),
        invalid_type_error: t('session.newSessionModal.validation.type.required'),
      }),
      location: z.string().nullable().default(null),
      schedule: z
        .string({ required_error: t('session.newSessionModal.validation.schedule.required') })
        .min(1, t('session.newSessionModal.validation.schedule.required'))
        .regex(/^([01]\d|2[0-3]):[0-5]\d$/, t('session.newSessionModal.validation.schedule.invalid')),
      duration: z
        .string({ required_error: t('session.newSessionModal.validation.duration.required') })
        .min(1, t('session.newSessionModal.validation.duration.required')),
    })
    .refine((data) => data.type !== 'in-person' || !!data.location, {
      message: t('session.newSessionModal.validation.location.requiredIfInPerson'),
      path: ['location'],
    }),
)

const { handleSubmit, values, setFieldValue } = useForm({
  validationSchema: formSchema,
  initialValues: {
    name: null,
    groupId: tempSelectedGroupIdRef.value ?? sessionRef.value?.mentoringGroupId ?? undefined,
    objectiveId: tempSelectedObjectiveIdRef.value ?? null,
    date: undefined,
    type: undefined,
    location: null,
    schedule: undefined,
    duration: undefined,
  },
})

const groupIdForObjectivesRef = computed(() => values.groupId ?? sessionRef.value?.mentoringGroupId ?? null)
const { groupRef: selectedGroupRef } = useGroup(groupIdForObjectivesRef)

type GroupOptionType = { id: string; name: string }
function orderGroupsByName(groups: GroupOptionType[]) {
  return [...groups].sort((a, b) => (a.name && b.name ? a.name.localeCompare(b.name) : 0))
}
const orderedGroupsRef = computed(() =>
  orderGroupsByName(groupsRef.value.filter((group) => group.users.some((u) => getMentor(u)))),
)
const groupsOptionsRef = computed(() => orderedGroupsRef.value.map((group) => ({ value: group.id, label: group.name })))

const { data: objectivesDataRef } = useQuery({
  queryKey: [SESSION_OBJECTIVES_QUERY_KEY, 'progress', 'group', groupIdForObjectivesRef],
  queryFn: () => {
    const groupId = groupIdForObjectivesRef.value
    if (!groupId) return Promise.resolve({ objectives: [] })
    return getSessionObjectivesWithProgress(groupId)
  },
  enabled: computed(() => !!groupIdForObjectivesRef.value),
})

const objectiveItemsRef = computed(() =>
  (objectivesDataRef.value?.objectives ?? []).map((obj: any) => ({
    value: obj.id,
    label: `${t('dashboard.shared.programObjectives.objectiveNumber', { number: obj.order + 1 })} - ${obj.title}`,
  })),
)

const menteesRef = computed(() => selectedGroupRef.value?.users.filter(getMentees) ?? [])

function userInitials(user: { firstName?: string | null; lastName?: string | null }) {
  const first = user.firstName?.[0] ?? ''
  const last = user.lastName?.[0] ?? ''
  return (first + last).toUpperCase() || '?'
}

const menteesDisplayLabel = computed(() => {
  const count = menteesRef.value.length
  if (count === 0) return ''
  const names = menteesRef.value
    .slice(0, 3)
    .map((m: any) => `${m.firstName ?? ''} ${(m.lastName ?? '')[0] ?? ''}.`.trim())
    .join(', ')
  if (count > 3) return `${count} ${t('common.mentee.capitalize', count).toLowerCase()} · ${names}…`
  return `${count} ${t('common.mentee.capitalize', count).toLowerCase()} · ${names}`
})

const endTimeRef = computed(() => {
  if (!values.date || !values.schedule || !values.duration) return null
  const from = dayjsClient(`${values.date}T${values.schedule}`)
  if (!from.isValid()) return null
  return from.add(Number(values.duration), 'h').format('HH:mm')
})

// Reset temp objective when closing modal
watch(
  () => modalsStore.modals['new-session']?.isOpen,
  (isOpen) => {
    if (!isOpen) groupsStore.setTempSelectedObjectiveId(null)
  },
)

// Hydrate form from session in edit mode
watch(
  sessionRef,
  (session) => {
    if (!session) return
    setFieldValue('name', session.name ?? null)
    setFieldValue('groupId', session.mentoringGroupId)
    setFieldValue('date', dayjsClient(session.from).format('YYYY-MM-DD'))
    setFieldValue('type', session.location === 'visio' ? 'visio' : 'in-person')
    setFieldValue('location', session.location === 'visio' ? null : session.location)
    setFieldValue('schedule', dayjsClient(session.from).format('HH:mm'))
    setFieldValue('duration', String(dayjsClient(session.to).diff(dayjsClient(session.from), 'minutes') / 60))
  },
  { immediate: true },
)

const isSubmittingRef = ref(false)

// session passee dont la carte vient d'etre postee : son detail s'ouvre une fois
// dans le chat. Garde ici et non dans les options de mutate(), qui ne sont pas
// appelees si la modale (donc ce composant) est deja fermee.
const pastSessionIdToOpenRef = ref<string | null>(null)

const { mutate: sendMessage } = useMutation({
  mutationFn: sendMessageMutation,
  onError: () => toast.error(t('error.common')),
  onSuccess: async (_data, variables) => {
    const pastSessionId = pastSessionIdToOpenRef.value
    pastSessionIdToOpenRef.value = null
    await invalidateSessions()
    await invalidateConversations()
    await router?.push(ROUTES.CHAT(variables.conversationId))
    modalsStore.toggleModal('new-session', false)
    if (pastSessionId) {
      modalsStore.updateModalAttachedData<SessionDetailsModalType>('session-details', { sessionId: pastSessionId })
      modalsStore.toggleModal('session-details', true)
    }
  },
  onSettled: () => {
    isSubmittingRef.value = false
  },
})

const { mutate: createSession } = useMutation({
  mutationFn: V1CreateSessionMutation,
  onError: (err) => {
    if (err.message.includes('overlaps')) toast.error(t('session.newSessionModal.error.overlap'))
    else toast.error(t('error.common'))
    isSubmittingRef.value = false
  },
  onSuccess: async (data) => {
    await invalidateSessions()
    await invalidateSessionObjectives()
    toast.success(t('session.newSessionModal.success.create'))

    const conversationId = selectedGroupRef.value?.conversationId
    if (!conversationId) throw new Error('Conversation id is missing')
    // la carte part dans le chat du groupe dans tous les cas, y compris pour une
    // session deja commencee ou declaree apres coup, pour que le groupe la voie
    if (dayjsClient(data.session.from).isBefore(dayjsClient())) pastSessionIdToOpenRef.value = data.session.id
    sendMessage({ conversationId, message: `***session=${data.session.id}` })
  },
})

const { mutate: updateSession } = useMutation({
  mutationFn: V1UpdateSessionMutation,
  onError: () => toast.error(t('session.newSessionModal.error.update')),
  onSuccess: async () => {
    await invalidateSessions()
    await invalidateSession(sessionIdRef.value!)
    await invalidateSessionObjectives()
    toast.success(t('session.newSessionModal.success.update'))
    modalsStore.updateModalAttachedData<EditSessionModalType>('edit-session', { sessionId: null })
    modalsStore.toggleModal('edit-session', false)
  },
})

function onClose() {
  if (sessionIdRef.value) {
    modalsStore.toggleModal('edit-session', false)
    modalsStore.updateModalAttachedData<EditSessionModalType>('edit-session', { sessionId: null })
  } else {
    modalsStore.toggleModal('new-session', false)
  }
}

const onSubmit = handleSubmit(
  (data) => {
    isSubmittingRef.value = true
    const fromDate = dayjsClient(`${data.date}T${data.schedule}`).toISOString()
    const toDate = dayjsClient(fromDate).add(Number(data.duration), 'h').toISOString()
    const location = data.type === 'in-person' ? data.location! : data.type

    if (sessionIdRef.value) {
      updateSession({
        sessionId: sessionIdRef.value,
        startAt: fromDate,
        endAt: toDate,
        name: data.name,
        mentoringGroupId: data.groupId,
        location,
      })
      return
    }

    const payload: any = {
      userId: accountRef.value!.id!,
      from: fromDate,
      to: toDate,
      name: data.name,
      mentoringGroupId: data.groupId,
      location,
    }
    if (data.objectiveId) payload.sessionObjectiveId = data.objectiveId

    createSession(payload)
  },
  () => {
    toast.error(t('session.newSessionModal.validation.formIncomplete'))
  },
)
</script>
